import { ADMINISTRATOR_ROLE } from '../config';
import { ensureCurrentUser } from './data';
import { getCurrentUserLocation } from './localStorage';

/**
 * Check if a user is absent (no id), deleted, or banned
 * @param {*} currentUser
 */
export const userIsDeletedOrBanned = currentUser =>
    currentUser &&
    currentUser.attributes &&
    (currentUser.attributes.deleted || currentUser.attributes.banned);

/**
 * Check if a user is absent (no id), deleted, or banned
 * @param {*} currentUser
 */
export const userIsNotAvailable = currentUser =>
    !currentUser || !currentUser.id || userIsDeletedOrBanned(currentUser);

export const calculateAge = birthObj => {
    const { day, month, year } = birthObj;
    const birthday = new Date([month, day, year].join('/'));

    const ageDifMs = Date.now() - birthday.getTime();
    const ageDate = new Date(ageDifMs);
    return Math.abs(ageDate.getUTCFullYear() - 1970);
};

export const isProfileComplete = currentUser => {
    if (userIsNotAvailable(currentUser)) return {};

    const { attributes, profileImage } = currentUser || { attributes: {} };
    const { profile, emailVerified } = attributes;
    const { id: imageId } = profileImage || {};

    const {
        bio,
        publicData: {
            availability,
            interest,
            desiredDisciplines,
            skillsGeneral,
            skillsExperience,
            languages,
            phoneVerified,
            hasGalleryAssets,
        },
    } = profile;

    const isPrimaryFieldsComplete =
        !!availability &&
        !!profileImage &&
        !!imageId &&
        !!bio &&
        !!interest &&
        interest.length > 0 &&
        !!desiredDisciplines &&
        desiredDisciplines.length > 0;

    const skillsAreFilled =
        Boolean(skillsGeneral && skillsGeneral.length > 0) ||
        Boolean(skillsExperience && skillsExperience.length > 0);

    return {
        profileComplete:
            isPrimaryFieldsComplete &&
            skillsAreFilled &&
            !!languages &&
            Object.values(languages || {}).length > 0 &&
            emailVerified &&
            phoneVerified &&
            !!hasGalleryAssets,
        isPrimaryFieldsComplete,
    };
};
/**
 * Resolve lat lng string from either user public data or LS value
 * @param {*} user sdk user
 * @returns null | string
 */
export const resolveUserLatLng = user => {
    if (user?.attributes?.profile?.publicData?.userLocation) {
        const { userLocation } = user.attributes.profile.publicData;

        const userLocationStorage =
            userLocation && userLocation.lat && userLocation.lng ? userLocation : null;

        if (!userLocationStorage) {
            return '';
        }

        const { lat, lng } = userLocationStorage;
        return `${lat},${lng}`;
    }
    const userLSData = getCurrentUserLocation();

    if (userLSData && userLSData.loc) {
        return userLSData.loc;
    }

    return null;
};

export const SCORE_TOTAL = 'total';
export const SCORE_SUFFICIENT = 'sufficient';
export const SCORE_INSUFFICIENT = 'insufficient';

/**
 * total, sufficient, insufficient
 * @param {Number} score
 * @returns
 */
export const resolveStatusByMatchingMatrixScore = score =>
    score >= 70 ? SCORE_TOTAL : score > 30 && score <= 69 ? SCORE_SUFFICIENT : SCORE_INSUFFICIENT;

export const isAdmin = currentUser => {
    if (!currentUser || !currentUser.id) {
        return false;
    }

    const {
        attributes: {
            profile: {
                privateData: { role },
            },
        },
    } = ensureCurrentUser(currentUser);

    return role === ADMINISTRATOR_ROLE;
};
/**
 * Check if the current user has blocked or been blocked by another user
 *
 * @param {object} currentUser - sdk current user
 * @param {string} otherPartyId - uuid of a user being blocked
 * @returns boolean
 */
export const isOtherUserBlocked = (currentUser, otherPartyId) => {
    const blockedUsers = currentUser?.attributes?.profile?.privateData?.blockedUsers || {};

    return Boolean(blockedUsers[otherPartyId]);
};

export const isEmailNotVerified = currentUser => {
    const {
        attributes: { emailVerified, email },
    } = ensureCurrentUser(currentUser);

    return email && !emailVerified;
};

export const getCreditsHistoryByType = currentUser => {
    const { userType } = currentUser?.attributes?.profile?.publicData || {};
    const { creditsHistory = {} } = currentUser?.attributes?.profile?.privateData || {};

    const creditsHistoryByType = creditsHistory[userType] || [];

    return creditsHistoryByType;
};
